<template>
    <div class="fa-picker">
        <div class="picker-head row g-3">
            <div class="picker-preview col-2">
                <span class="preview-icon" v-html="normalizedValue || fallbackGlyph"></span>
            </div>
            <div class="picker-input col-3">
                <golia-input :readonly="true" v-model="manualValue" :label="'Glyph'" :placeholder="'&#xe028; / e028'"
                    @input="handleManualInput" />
            </div>
            <div class="picker-button col">
                <golia-button variant="btn-fill dark-blue" icon="fa-fw fa-icons"
                    :label="__('manage_poi.categories.choose_icon')" v-b-modal="modalId" />
            </div>
        </div>

        <!-- <small class="text-muted d-block mt-2">
            {{ normalizedValue || fallbackGlyph }}
        </small> -->

        <b-modal :id="modalId" title="Font Awesome 6 Pro" size="xl" body-class="custom-scrollbar">
            <div class="picker-modal">
                <golia-input v-model="search" :placeholder="__('manage_poi.categories.search_icon')" />

                <div class="picker-status mt-2">
                    <span v-if="loading">Loading icons...</span>
                    <span v-else>{{ filteredIcons.length }} icons</span>
                </div>

                <div class="icon-grid mt-3" v-if="filteredIcons.length">
                    <button v-for="icon in filteredIcons" :key="icon.key" type="button" class="icon-tile"
                        :class="{ selected: icon.fa === normalizedValue }" @click="selectIcon(icon.fa)">
                        <span class="icon-glyph" v-html="icon.fa"></span>
                        <span class="icon-name">{{ icon.name }}</span>
                        <span class="icon-code">{{ icon.unicodeHex }}</span>
                    </button>
                </div>

                <div class="text-muted mt-3" v-else-if="!loading">
                    {{ __('manage_poi.categories.no_icons') }}
                </div>
            </div>

            <template #modal-footer="{ cancel }">
                <golia-button @click="cancel()" :label="__('manage_poi.categories.cancel')" variant="btn-fill gray" />
            </template>
        </b-modal>
    </div>
</template>

<script>
    import iconCatalog from '@/data/fontawesome-pro-icons.json'

    const fallbackGlyph = '&#xf128;'

    function normalizeGlyph(value) {
        if (!value) return ''

        const trimmed = String(value).trim()
        if (!trimmed) return ''

        const entityMatch = trimmed.match(/^&#x?([a-f0-9]+);?$/i)
        if (entityMatch) return `&#x${entityMatch[1].toLowerCase()};`

        const hexMatch = trimmed.match(/^[a-f0-9]+$/i)
        if (hexMatch) return `&#x${trimmed.toLowerCase()};`

        return trimmed
    }

    export default {
        name: 'FontAwesomeGlyphPicker',
        props: {
            value: {
                type: String,
                default: ''
            },
            modalId: {
                type: String,
                default: 'fa-glyph-picker-modal'
            }
        },
        data() {
            return {
                loading: false,
                search: '',
                icons: iconCatalog,
                manualValue: this.value || '',
                fallbackGlyph
            }
        },
        computed: {
            normalizedValue() {
                return normalizeGlyph(this.value)
            },
            filteredIcons() {
                const query = this.search.trim().toLowerCase()
                if (!query) return this.icons

                return this.icons.filter((icon) => {
                    return (
                        icon.name.includes(query) ||
                        icon.unicodeHex.includes(query) ||
                        icon.fa.toLowerCase().includes(query)
                    )
                })
            }
        },
        methods: {
            handleManualInput() {
                const normalized = normalizeGlyph(this.manualValue)
                this.$emit('input', normalized)
            },
            selectIcon(value) {
                const normalized = normalizeGlyph(value)
                this.manualValue = normalized
                this.$emit('input', normalized)
                this.$bvModal.hide(this.modalId)
            }
        },
        watch: {
            value: {
                handler(val) {
                    this.manualValue = val || ''
                },
                immediate: true
            }
        }
    }
</script>

<style scoped lang="scss">
    .picker-head {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 12px;
        --bs-gutter-x: 0;
    }

    .picker-preview {
        width: 35px;
        height: 35px;
        border: 1px solid #d6dbe1;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
    }

    .preview-icon,
    .icon-glyph {
        font-family: 'Font Awesome Kit', 'FontAwesome', 'Font Awesome 6 Pro';
        font-size: 16px;
        line-height: 1;
    }

    .picker-actions {
        flex: 1;
        display: flex;
        gap: 12px;
        align-items: flex-end;
    }

    .picker-actions>*:first-child {
        flex: 1;
    }

    .icon-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 12px;
        max-height: 60vh;
        overflow: auto;
    }

    .icon-tile {
        border: 1px solid #d6dbe1;
        border-radius: 12px;
        background: #fff;
        padding: 12px 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        min-height: 100px;
    }

    .icon-tile.selected {
        border-color: #1d5f9f;
        box-shadow: 0 0 0 2px rgba(29, 95, 159, 0.12);
    }

    .icon-name,
    .icon-code {
        font-size: 11px;
        text-align: center;
        word-break: break-word;
    }

    .icon-code {
        color: #7b8794;
    }
</style>
